/**
 * Accuracy weight per stage — how authoritative/reliable a hit from that stage
 * is. Overrides are curated (deterministic); lexical/synonym are strong; fuzzy
 * (edit-distance) and graph (related-expansion) are weaker signals.
 */
export const STAGE_WEIGHT = {
    override: 1.0,
    synonym: 0.9,
    lexical: 0.85,
    prefix: 0.82,
    vector: 0.8,
    fuzzy: 0.6,
    graph: 0.5,
};
export function clamp01(n) {
    return Math.max(0, Math.min(1, n));
}
/** Per-code accumulation of every stage's score plus the boolean flags. */
export class QueryAggregate {
    lexical = 0;
    prefix = 0;
    fuzzy = 0;
    graph = 0;
    vector = 0;
    synonym = false;
    override = false;
}
/**
 * Run the ordered retrieval stages and merge their scores into one aggregate
 * per code. The `synonym` flag is derived from the lexical pool: a code that
 * matched only via synonym expansion (present in the expanded BM25 pool but
 * absent from the raw pool) is a synonym hit.
 */
export function aggregate(stages, ctx, view) {
    const byCode = new Map();
    const ensure = (code) => {
        let a = byCode.get(code);
        if (!a) {
            a = new QueryAggregate();
            byCode.set(code, a);
        }
        return a;
    };
    for (const stage of stages) {
        const scores = stage.retrieve(ctx, view);
        for (const [code, score] of scores) {
            const a = ensure(code);
            switch (stage.stage) {
                case 'lexical':
                    a.lexical = Math.max(a.lexical, score);
                    break;
                case 'prefix':
                    a.prefix = Math.max(a.prefix, score);
                    break;
                case 'fuzzy':
                    a.fuzzy = Math.max(a.fuzzy, score);
                    break;
                case 'graph':
                    a.graph = Math.max(a.graph, score);
                    break;
                case 'vector':
                    a.vector = Math.max(a.vector, score);
                    break;
                default: break;
            }
        }
    }
    for (const r of ctx.lexicalResults) {
        if (!ctx.rawCodes.has(r.code)) {
            const a = byCode.get(r.code);
            if (a)
                a.synonym = true;
        }
    }
    return byCode;
}
/**
 * Build a single rerankable {@link SearchResult} from an aggregate. Computes
 * the dominant `primaryStage` (highest `STAGE_WEIGHT` among those present),
 * `confidence` for that stage, and `rerankScore` (stage × confidence ×
 * relevance). Single source of truth so every search path emits a consistent
 * shape.
 */
export function buildResult(code, agg, weights, view) {
    const doc = view.docByCode.get(code);
    if (!doc)
        return null;
    const { vector, lexical, graph, fuzzy, prefix } = agg;
    const present = [];
    if (agg.override)
        present.push('override');
    if (agg.synonym)
        present.push('synonym');
    if (lexical > 0)
        present.push('lexical');
    if (prefix > 0)
        present.push('prefix');
    if (fuzzy > 0)
        present.push('fuzzy');
    if (vector > 0)
        present.push('vector');
    if (graph > 0)
        present.push('graph');
    const primaryStage = present.length
        ? present.sort((a, b) => STAGE_WEIGHT[b] - STAGE_WEIGHT[a])[0]
        : 'lexical';
    let confidence;
    switch (primaryStage) {
        case 'override':
            confidence = 0.99;
            break;
        case 'vector':
            confidence = clamp01(vector);
            break;
        case 'lexical':
            confidence = clamp01(lexical);
            break;
        case 'prefix':
            confidence = clamp01(prefix);
            break;
        case 'synonym':
            confidence = clamp01(lexical) * 0.95;
            break;
        case 'fuzzy':
            confidence = clamp01(fuzzy);
            break;
        case 'graph':
            confidence = clamp01(graph);
            break;
    }
    const relevance = clamp01(Math.max(lexical, vector, graph, fuzzy, prefix));
    const rerankScore = STAGE_WEIGHT[primaryStage] * confidence * (0.7 + 0.3 * relevance);
    const score = weights.vector * vector + weights.lexical * (lexical + prefix) + weights.graph * graph;
    return {
        code,
        description: doc.description,
        majorGroup: doc.majorGroupTitle,
        minorGroup: doc.minorGroupTitle,
        skillLevel: doc.skillLevel,
        score,
        rerankScore,
        confidence,
        primaryStage,
        stages: present,
        vectorScore: vector,
        lexicalScore: lexical,
        graphScore: graph,
        fuzzyScore: fuzzy,
        prefixScore: prefix,
        tasks: doc.tasks,
        alternateTitles: doc.alternateTitles,
        generatedDescription: doc.generatedDescription,
    };
}
/**
 * Rank aggregated stage hits into final {@link SearchResult}s and pin phrase
 * overrides to the top. Override codes are pinned deterministically (curated
 * for casual SA phrasing retrieval cannot surface, e.g. "code 14 driver") —
 * injected at the very top, promoted in place when already retrieved.
 */
export function rankPipeline(ctx, view, byCode, overrides, opts) {
    const { weights, limit, promoteExisting } = opts;
    const results = [];
    for (const [code, agg] of byCode) {
        const r = buildResult(code, agg, weights, view);
        if (r)
            results.push(r);
    }
    const overrideResults = [];
    if (overrides && overrides.length) {
        const seen = new Set(results.map((r) => r.code));
        for (const code of overrides) {
            const doc = view.docByCode.get(code);
            if (!doc)
                continue;
            if (seen.has(code)) {
                if (!promoteExisting)
                    continue;
                // Already retrieved — promote it to the top as the canonical match.
                const existing = results.find((r) => r.code === code);
                existing.primaryStage = 'override';
                if (!existing.stages.includes('override'))
                    existing.stages.push('override');
                existing.rerankScore = STAGE_WEIGHT.override * 0.99;
                existing.confidence = 0.99;
                existing.score = 1.1;
                existing.lexicalScore = 1.1;
                continue;
            }
            seen.add(code);
            overrideResults.push({
                code: doc.code,
                description: doc.description,
                majorGroup: doc.majorGroupTitle,
                minorGroup: doc.minorGroupTitle,
                skillLevel: doc.skillLevel,
                score: 1.1,
                rerankScore: STAGE_WEIGHT.override * 0.99,
                confidence: 0.99,
                primaryStage: 'override',
                stages: ['override'],
                vectorScore: 0,
                lexicalScore: 1.1,
                graphScore: 0,
                fuzzyScore: 0,
                prefixScore: 0,
                tasks: doc.tasks,
                alternateTitles: doc.alternateTitles,
            });
        }
    }
    results.sort((a, b) => b.rerankScore - a.rerankScore);
    return [...overrideResults, ...results].slice(0, limit);
}
//# sourceMappingURL=pipeline.js.map